/**
 * useCritiqueUrlState Hook: Critique View URL State Management
 * Feature: 040-critique-url-state
 *
 * Main URL state management hook for critique view.
 * Synchronizes URL query parameters with photo/comment selection state.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseUrlParams, buildPhotoUrl, buildPhotoCommentUrl, createUrlState } from '../utils/urlState';
import { usePhotoFromCommentGraphQL } from './graphql/usePhotoFromCommentGraphQL';
import type { UseCritiqueUrlStateReturn } from '../types/critiqueUrlState';

/**
 * Main URL state management hook
 *
 * Synchronizes URL query parameters with critique view state.
 * Handles photo selection, comment highlighting, and comment-to-photo lookup.
 *
 * @param collectionId - Global ID of the collection being viewed
 * @param photos - Array of photos in the collection (for validation)
 * @returns URL state and update functions
 *
 * @example
 * ```tsx
 * function CollectionCritiqueModePage() {
 *   const { collectionId } = useParams();
 *   const { data: photos } = useCollectionPhotos(collectionId);
 *
 *   const {
 *     state,
 *     selectPhoto,
 *     selectComment,
 *     deselectComment,
 *     isLoading
 *   } = useCritiqueUrlState(collectionId, photos);
 *
 *   // Use state.photoId to determine selected photo
 *   // Use state.commentId to determine highlighted comment
 *   // Call selectPhoto() when user clicks a photo
 * }
 * ```
 *
 * Behavior:
 * - On mount: Parse URL parameters, validate against photos array
 * - If ?comment=<id> but no ?photo=<id>: Fetch comment to determine photo
 * - Update URL when selectPhoto/selectComment called (creates history entry)
 * - Listen to URL changes (browser back/forward) and update state
 * - Validate all IDs against global ID format
 * - Handle invalid/missing photos gracefully
 */
export function useCritiqueUrlState(
  collectionId: string,
  photos: Array<{ id?: string; photoData?: { id: string } }> | undefined
): UseCritiqueUrlStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const parsed = parseUrlParams(searchParams);

  // Fetch photo ID if we have comment but no photo
  const shouldFetchPhotoFromComment = !!parsed.commentId && !parsed.photoId;
  const { photoId: photoFromComment, isLoading } = usePhotoFromCommentGraphQL(
    collectionId,
    shouldFetchPhotoFromComment ? parsed.commentId : null
  );

  // Determine final photo ID (from URL or comment lookup)
  const finalPhotoId = parsed.photoId || photoFromComment || null;

  // Create final state with validation
  const state = createUrlState(
    {
      ...parsed,
      photoId: finalPhotoId,
    },
    photos
  );

  // Update URL when comment-to-photo lookup completes
  useEffect(() => {
    if (photoFromComment && parsed.commentId && !parsed.photoId) {
      // Replace history entry (don't create new entry for automatic lookup)
      setSearchParams(buildPhotoCommentUrl(photoFromComment, parsed.commentId), { replace: true });
    }
  }, [photoFromComment, parsed.commentId, parsed.photoId, setSearchParams]);

  /**
   * Select a photo (updates ?photo parameter, removes ?comment)
   * Replaces current history entry to avoid cluttering browser back stack.
   */
  const selectPhoto = (photoId: string) => {
    setSearchParams(buildPhotoUrl(photoId), { replace: true });
  };

  /**
   * Select a comment (updates both ?photo and ?comment parameters)
   * Replaces current history entry to avoid cluttering browser back stack.
   * Optionally triggers scroll (handled by useScrollToCritique hook).
   */
  const selectComment = (photoId: string, commentId: string, _shouldScroll?: boolean) => {
    // Note: shouldScroll parameter is available for future use but
    // scrolling is currently handled by useScrollToCritique hook
    setSearchParams(buildPhotoCommentUrl(photoId, commentId), { replace: true });
  };

  /**
   * Deselect comment (removes ?comment parameter, keeps ?photo)
   * Replaces current history entry to avoid cluttering browser back stack.
   */
  const deselectComment = () => {
    if (state.photoId) {
      setSearchParams(buildPhotoUrl(state.photoId), { replace: true });
    }
  };

  /**
   * Clear all URL parameters
   * Creates new history entry.
   */
  const clearAll = () => {
    setSearchParams({});
  };

  return {
    state,
    selectPhoto,
    selectComment,
    deselectComment,
    clearAll,
    isLoading,
  };
}
